import { useEffect, useState } from "react";
import style from "./share-canvas-modal-new.module.css";
import { Team } from "shared/datamodel/schemas/team";
import { Project } from "shared/datamodel/schemas/project";
import { MemberRow } from "../collaboration/member-row";
import Skeleton from "react-loading-skeleton";
import { InstanceType } from "shared/datamodel/schemas/ability";
import { useInstanceMembers } from "frontend/hooks/use-instance-members";
import { InstanceLine } from "../collaboration/instance-line";
import { Board } from "shared/datamodel/schemas/board";
import { User } from "shared/datamodel/schemas/user";
import ShareModalUserCell from "../share-user-model-cell";
import { useUsersCache } from "frontend/hooks/caching/use-users-cache";

export default function PeopleWithAccessToCanvas({
  board,
  parentInstance,
  openMembersModal,
}: {
  board: Board;
  parentInstance: Team | Project | null;
  openMembersModal: () => void;
}) {
  const [canvasOwner, setCanvasOwner] = useState<User | null>(null);
  const { members: boardMembers, isLoading: isBoardMembersLoading } = useInstanceMembers(
    board.documentId,
    InstanceType.Board
  );
  const { getUsers } = useUsersCache();

  useEffect(() => {
    getUsers([board.createdByUserId?.toString() ?? ""]).then((users) => {
      if (users.length > 0) {
        setCanvasOwner(users[0]);
      }
    });
  }, [getUsers, board.createdByUserId]);

  function renderOwner() {
    return (
      <div className={style.userLine}>
        <div className={style.listUser}>
          <ShareModalUserCell user={canvasOwner as User} />
        </div>
        <div className={style.userPermission}>
          <div className={style.permission}>
            <span>Owner</span>
          </div>
        </div>
      </div>
    );
  }

  function renderParentInstanceName() {
    if (board.projectDocumentId && parentInstance) {
      return (
        <InstanceLine
          name={parentInstance.name}
          openMembersModal={openMembersModal}
          membersCount={(parentInstance as Project).userProjectPermissions.length}
        />
      );
    } else if (board.teamDocumentId && parentInstance) {
      return (
        <InstanceLine
          name={parentInstance.name}
          openMembersModal={openMembersModal}
          membersCount={(parentInstance as Team).userTeamPermissions.length}
        />
      );
    } else {
      return null;
    }
  }

  const renderMembers = () => {
    return boardMembers?.map(
      (teammate, index) =>
        teammate.id !== canvasOwner?.id && (
          <MemberRow
            key={teammate.id}
            teammate={teammate}
            members={boardMembers}
            index={index}
            isOwner={board.isOwner}
            instance={{ ...board, members: boardMembers.map((m) => ({ id: Number(m.id), permission: m.permission })) }}
            instanceType={InstanceType.Board}
          />
        )
    );
  };

  return (
    <div className={style.collaborators}>
      {isBoardMembersLoading || !canvasOwner ? (
        <Skeleton count={4} className={style.userLineSkeleton} style={{ minHeight: "36px", margin: "4px 0" }} />
      ) : (
        <>
          {renderOwner()}
          {renderMembers()}
          {renderParentInstanceName()}
        </>
      )}
    </div>
  );
}
